/**
 * Hybrid photogrammetry: guided capture → Meshroom ZIP → CAD packs
 * + hole-closing checklist (Blender / Open3D).
 *
 * Note: multi-photo “Astra-perfect” product/property demos are usually
 * generative image-to-3D / multi-view AI — different from metric Meshroom meshes.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Smartphone, Monitor, Upload, Download, Camera, Box, ExternalLink,
  CheckCircle2, AlertTriangle, Trash2, FileCode, ArrowRight, Video,
  Circle, Square, Lightbulb, Sparkles, Wrench, Scale, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { createStoreZip, dataUrlToUint8 } from "@/lib/storeZip";
import { getBaseUrl, getAuthToken } from "@/lib/apiClient";

const STORE_KEY = "prometheus_photogrammetry_assets";
const FRAMES_KEY = "prometheus_photogrammetry_frames";

export type ScanSource = "lidar_iphone" | "meshroom_pc" | "guided_cam" | "other";
export type CadTarget = "fusion360" | "freecad" | "blender" | "bambu";

export type PhotogrammetryAsset = {
  id: string;
  name: string;
  source: ScanSource;
  format: string;
  sizeBytes: number;
  createdAt: string;
  dataUrl: string;
  notes?: string;
};

type CaptureFrame = {
  id: string;
  dataUrl: string;
  tip: string;
  at: string;
};

const FORMAT_GUIDE: Record<
  CadTarget,
  { title: string; preferred: string[]; tip: string; importHint: string }
> = {
  fusion360: {
    title: "Fusion 360",
    preferred: ["OBJ", "STL"],
    tip: "Insert Mesh. Mesh to BRep solo si la malla es limpia. El escaneo no es STEP paramétrico.",
    importHint: "Insert → Insert Mesh → mm.",
  },
  freecad: {
    title: "FreeCAD",
    preferred: ["OBJ", "STL"],
    tip: "Mesh workbench → opcional Part Shape. Agujeros: cierra antes en Blender si puedes.",
    importHint: "File → Import → OBJ/STL.",
  },
  blender: {
    title: "Blender",
    preferred: ["GLB", "OBJ", "STL"],
    tip: "Mejor destino nativo. Voxel Remesh / Fill Holes / Decimate aquí.",
    importHint: "File → Import → glTF / OBJ / STL.",
  },
  bambu: {
    title: "Bambu Studio / Orca",
    preferred: ["STL", "OBJ"],
    tip: "Exporta STL limpio desde Blender tras remesh.",
    importHint: "Add → STL/OBJ · revisa escala mm.",
  },
};

const CAPTURE_TIPS = [
  { min: 0, tip: "Empieza de frente, cámara estable, luz pareja (evita contraluz fuerte)." },
  { min: 3, tip: "Gira ~15–20° alrededor del objeto. Overlap alto entre fotos." },
  { min: 8, tip: "Baja un poco el ángulo: captura la base / bordes inferiores." },
  { min: 14, tip: "Sube el ángulo: tapa superior y esquinas altas." },
  { min: 20, tip: "Detalle: acercamientos a tornillos, juntas, texturas mates." },
  { min: 28, tip: "Cierra el círculo: vuelve al frente y revisa caras brillantes (mata reflejos)." },
  { min: 36, tip: "Excelente cobertura. Exporta ZIP a Meshroom cuando pases de ~40 frames." },
];

const HOLE_CHECKLIST = [
  {
    id: "inspect",
    title: "Inspeccionar huecos",
    detail: "Blender: Edit Mode → Select → Select All by Trait → Non Manifold. Marca agujeros.",
  },
  {
    id: "fill",
    title: "Cerrar agujeros simples",
    detail: "Blender: seleccionar borde del hueco → F (fill) o Grid Fill. Evita rellenar zonas enormes a ciegas.",
  },
  {
    id: "remesh",
    title: "Remesh / densificar",
    detail: "Voxel Remesh o Quad Remesh para uniformar. Open3D: Poisson / Ball Pivoting tras filtrar outliers.",
  },
  {
    id: "decimate",
    title: "Aligerar para Fusion",
    detail: "Decimate Modifier (ratio 0.1–0.3) antes de Mesh to BRep. Fusion odia millones de triángulos.",
  },
  {
    id: "ref_model",
    title: "Modelar encima (recomendado CAD)",
    detail: "Usa la malla como referencia y crea sólidos en Fusion/FreeCAD para BOM mecanizable.",
  },
];

function currentTip(n: number): string {
  let tip = CAPTURE_TIPS[0].tip;
  for (const t of CAPTURE_TIPS) {
    if (n >= t.min) tip = t.tip;
  }
  return tip;
}

function loadAssets(): PhotogrammetryAsset[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveAssets(assets: PhotogrammetryAsset[]) {
  localStorage.setItem(STORE_KEY, JSON.stringify(assets.slice(0, 12)));
}

function loadFrames(): CaptureFrame[] {
  try {
    return JSON.parse(sessionStorage.getItem(FRAMES_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveFrames(frames: CaptureFrame[]) {
  // sessionStorage: avoids filling localStorage with dozens of JPEGs permanently
  try {
    sessionStorage.setItem(FRAMES_KEY, JSON.stringify(frames.slice(-80)));
  } catch {
    // quota — keep last 40
    try {
      sessionStorage.setItem(FRAMES_KEY, JSON.stringify(frames.slice(-40)));
    } catch {
      /* ignore */
    }
  }
}

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

function blenderImportScript(filename: string): string {
  return `# Prometheus → Blender
import bpy
path = r"${filename}"
ext = path.lower().rsplit(".", 1)[-1]
if ext in ("glb", "gltf"):
    bpy.ops.import_scene.gltf(filepath=path)
elif ext == "obj":
    bpy.ops.wm.obj_import(filepath=path)
elif ext == "stl":
    bpy.ops.wm.stl_import(filepath=path)
print("OK", path)
`;
}

function freecadImportScript(filename: string): string {
  return `import Mesh, FreeCAD as App
path = r"${filename}"
doc = App.ActiveDocument or App.newDocument("Prometheus_Scan")
obj = doc.addObject("Mesh::Feature", "Scan_Prometheus")
obj.Mesh = Mesh.Mesh(path)
doc.recompute()
`;
}

function fusionImportNotes(filename: string): string {
  return `# Fusion 360 — ${filename}
# Insert → Insert Mesh (OBJ/STL preferido). Unidades mm.
# Mesh to BRep solo con malla limpia y ligera (decimate antes).
`;
}

export function PhotogrammetryBridge() {
  const [assets, setAssets] = useState<PhotogrammetryAsset[]>(() => loadAssets());
  const [source, setSource] = useState<ScanSource>("guided_cam");
  const [cadTarget, setCadTarget] = useState<CadTarget>("blender");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [frames, setFrames] = useState<CaptureFrame[]>(() => loadFrames());
  const [camOn, setCamOn] = useState(false);
  const [holeDone, setHoleDone] = useState<Record<string, boolean>>({});
  const [showAstraNote, setShowAstraNote] = useState(true);
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState<{ dataUrl?: string; url?: string; prompt?: string; disclaimer?: string } | null>(null);
  const [abNotes, setAbNotes] = useState({ metric: "", generative: "", winner: "" });
  const [genKind, setGenKind] = useState<"product" | "property" | "structure">("product");
  const [genSubject, setGenSubject] = useState("pieza de taller");

  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    saveAssets(assets);
  }, [assets]);

  useEffect(() => {
    saveFrames(frames);
  }, [frames]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const selected = useMemo(
    () => assets.find((a) => a.id === selectedId) || assets[0] || null,
    [assets, selectedId]
  );
  const guide = FORMAT_GUIDE[cadTarget];
  const tip = currentTip(frames.length);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamOn(true);
      setSource("guided_cam");
      toast.success("Cámara lista — sigue el tip de captura");
    } catch (e: any) {
      toast.error(`No se pudo abrir la cámara: ${e?.message || e}`);
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCamOn(false);
  };

  const snapFrame = () => {
    const video = videoRef.current;
    if (!video || !camOn) {
      toast.warning("Enciende la cámara primero");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const frame: CaptureFrame = {
      id: `f_${Date.now()}`,
      dataUrl,
      tip,
      at: new Date().toISOString(),
    };
    setFrames((prev) => [...prev, frame].slice(-80));
    toast.success(`Frame ${frames.length + 1} · ${tip.slice(0, 48)}…`);
  };

  const clearFrames = () => {
    setFrames([]);
    sessionStorage.removeItem(FRAMES_KEY);
    toast.message("Frames borrados");
  };

  const exportMeshroomZip = async () => {
    if (frames.length < 8) {
      toast.warning("Recomendado: al menos ~8–15 fotos (ideal 40+). Sigue capturando.");
      if (frames.length === 0) return;
    }
    toast.info("Empaquetando ZIP para Meshroom…");
    try {
      const files = await Promise.all(
        frames.map(async (f, i) => {
          const data = await dataUrlToUint8(f.dataUrl);
          const n = String(i + 1).padStart(4, "0");
          return { name: `images/img_${n}.jpg`, data };
        })
      );
      const readme = new TextEncoder().encode(
        `Prometheus Guided Capture → Meshroom
Frames: ${frames.length}
Tips followed (last): ${tip}

Meshroom:
1. New project → Add Folder → carpeta "images" de este ZIP
2. Drawee el grafo default (o Draft + Texturing)
3. Compute → Export OBJ / glTF
4. Vuelve a Prometheus CAD → Importar malla → Pack Fusion/FreeCAD/Blender

Astra / LLM multi-foto:
- Sirve para GUÍA y para demos generativos (producto/propiedad “casi perfecto” visual).
- No sustituye la malla métrica de Meshroom para CAD.
`
      );
      files.push({ name: "README_PROMETHEUS.txt", data: readme });
      const zip = await createStoreZip(files);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadBlob(zip, `prometheus_meshroom_${stamp}.zip`);
      toast.success(`ZIP listo (${frames.length} fotos) → ábrelo en Meshroom`);
    } catch (e: any) {
      toast.error(`ZIP falló: ${e?.message || e}`);
    }
  };

  const onFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const next: PhotogrammetryAsset[] = [];
      for (const file of Array.from(files)) {
        const ext = extOf(file.name);
        if (!["glb", "gltf", "obj", "stl", "ply", "fbx"].includes(ext)) {
          toast.error(`Formato no soportado: .${ext}`);
          continue;
        }
        if (file.size > 80 * 1024 * 1024) {
          toast.error(`${file.name} > 80 MB`);
          continue;
        }
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.onerror = () => reject(r.error);
          r.readAsDataURL(file);
        });
        next.push({
          id: `scan_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          name: file.name,
          source,
          format: ext,
          sizeBytes: file.size,
          createdAt: new Date().toISOString(),
          dataUrl,
        });
      }
      if (!next.length) return;
      setAssets((prev) => {
        setSelectedId(next[0].id);
        return [...next, ...prev].slice(0, 12);
      });
      toast.success(`${next.length} malla(s) importada(s)`);
    },
    [source]
  );

  const downloadCadPack = () => {
    if (!selected) {
      toast.warning("Importa primero la malla de Meshroom/LiDAR");
      return;
    }
    downloadDataUrl(selected.dataUrl, selected.name);
    const base = selected.name.replace(/\.[^.]+$/, "") || "prometheus_scan";
    let body = "";
    let fname = "";
    if (cadTarget === "blender") {
      body = blenderImportScript(selected.name);
      fname = `${base}_import_blender.py`;
    } else if (cadTarget === "freecad") {
      body = freecadImportScript(selected.name);
      fname = `${base}_import_freecad.py`;
    } else if (cadTarget === "fusion360") {
      body = fusionImportNotes(selected.name);
      fname = `${base}_fusion_notes.txt`;
    } else {
      body = `# Slicer — usa STL/OBJ: ${selected.name}\n`;
      fname = `${base}_slicer.txt`;
    }
    downloadBlob(new Blob([body], { type: "text/plain" }), fname);
    toast.success(`Pack → ${guide.title}`);
  };

  const askPrometheusCoach = () => {
    const prompt =
      `Soy Angel en Puerto Rico. Estoy en captura guiada de fotogrametría en Prometheus ` +
      `con ${frames.length} frames. Último tip: "${tip}". ` +
      `Guíame como copiloto de escaneo (ángulos, overlap, salitre/reflejos, cuándo pasar a Meshroom). ` +
      `No inventes geometría 3D: solo coaching de captura. Si mencionas Astra/multi-foto generativo, ` +
      `aclara que eso es visual/demo, distinto de malla métrica para Fusion/FreeCAD.`;
    window.dispatchEvent(
      new CustomEvent("start-chat", {
        detail: { characterId: "prometheus", initialMessage: prompt },
      })
    );
    toast.success("Abriendo copiloto de captura en el chat");
  };

  const runGenerativeDemo = async () => {
    const srcFrames = frames.slice(-4);
    if (!srcFrames.length) {
      toast.warning("Captura al menos 1–4 fotos primero (o usa el ZIP y vuelve a cargar frames).");
      return;
    }
    setGenBusy(true);
    try {
      const res = await fetch(`${getBaseUrl()}/api/prometheus/photogrammetry/generative-demo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          subject: genSubject,
          kind: genKind,
          images_b64: srcFrames.map((f) => f.dataUrl),
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t.slice(0, 200) || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const g0 = (data.generated || [])[0] || {};
      setGenResult({
        dataUrl: g0.dataUrl,
        url: g0.url,
        prompt: data.astra_chat_prompt,
        disclaimer: data.disclaimer,
      });
      toast.success("Demo generativa lista — compárala con Meshroom (A/B)");
      if (data.astra_chat_prompt) {
        // keep prompt available; user can open chat
      }
    } catch (e: any) {
      toast.error(`Demo generativa: ${e?.message || e}`);
    } finally {
      setGenBusy(false);
    }
  };

  const openAstraPrompt = () => {
    if (!genResult?.prompt) {
      toast.message("Corre primero la demo generativa");
      return;
    }
    window.dispatchEvent(
      new CustomEvent("start-chat", {
        detail: { characterId: "prometheus", initialMessage: genResult.prompt },
      })
    );
  };

  return (
    <div className="rounded-2xl border border-cyan-500/25 bg-card/80 overflow-hidden">
      <div className="px-4 py-3 border-b border-border/40 bg-gradient-to-r from-cyan-950/40 to-orange-950/20 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Camera className="w-4 h-4 text-cyan-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">Fotogrametría guiada → Meshroom → CAD</h3>
            <p className="text-[10px] text-muted-foreground">
              Cámara + tips · ZIP Meshroom · cierre de huecos · Fusion / FreeCAD / Blender
            </p>
          </div>
        </div>
      </div>

      {showAstraNote && (
        <div className="mx-4 mt-3 p-3 rounded-xl border border-violet-500/25 bg-violet-500/10 text-[11px] text-violet-100/90 flex gap-2">
          <Sparkles className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1">
            <p className="font-bold text-violet-100">Lo que viste con Astra + varias fotos</p>
            <p className="text-violet-100/80 leading-relaxed">
              Suele ser <strong>generación multi‑vista / image‑to‑3D</strong> (producto o propiedad “casi perfecta” visual).
              Es genial para demos y concepto. <strong>Meshroom</strong> es la ruta métrica para llevar a Fusion/FreeCAD/Blender.
              Aquí usamos la IA como <strong>guía de captura</strong>, no como rellenador mágico de malla CAD.
            </p>
          </div>
          <button type="button" className="text-[10px] font-bold opacity-70 hover:opacity-100" onClick={() => setShowAstraNote(false)}>
            OK
          </button>
        </div>
      )}

      <div className="p-4 space-y-5">
        {/* A — Guided capture */}
        <section className="space-y-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Video className="w-3.5 h-3.5" /> A · Captura guiada (cámara)
          </p>

          <div className="grid lg:grid-cols-2 gap-3">
            <div className="relative rounded-xl overflow-hidden border border-border/50 bg-black aspect-video">
              <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
              {!camOn && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Camera className="w-8 h-8 opacity-40" />
                  <p className="text-[11px]">Enciende la cámara para empezar</p>
                </div>
              )}
              <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-2">
                {!camOn ? (
                  <button type="button" onClick={startCamera} className="h-8 px-3 rounded-lg bg-cyan-600 text-white text-[10px] font-bold">
                    Encender cámara
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={snapFrame} className="h-8 px-3 rounded-lg bg-orange-500 text-white text-[10px] font-bold inline-flex items-center gap-1">
                      <Circle className="w-3 h-3 fill-current" /> Capturar
                    </button>
                    <button type="button" onClick={stopCamera} className="h-8 px-3 rounded-lg bg-black/60 text-white text-[10px] font-bold inline-flex items-center gap-1">
                      <Square className="w-3 h-3" /> Stop
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 flex gap-2">
                <Lightbulb className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] font-bold text-amber-200 uppercase tracking-wide">
                    Tip · {frames.length} frame{frames.length === 1 ? "" : "s"}
                  </p>
                  <p className="text-[11px] text-amber-50/90 leading-snug mt-0.5">{tip}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={exportMeshroomZip}
                  disabled={frames.length === 0}
                  className="h-9 px-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-[11px] font-extrabold disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> ZIP → Meshroom
                </button>
                <button
                  type="button"
                  onClick={askPrometheusCoach}
                  className="h-9 px-3 rounded-xl border border-border/50 text-[11px] font-bold inline-flex items-center gap-1.5 hover:bg-muted/40"
                >
                  <Sparkles className="w-3.5 h-3.5 text-violet-300" /> Copiloto chat
                </button>
                <button
                  type="button"
                  onClick={clearFrames}
                  disabled={frames.length === 0}
                  className="h-9 px-3 rounded-xl text-[11px] font-bold text-muted-foreground disabled:opacity-40"
                >
                  Limpiar
                </button>
              </div>

              {frames.length > 0 && (
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {frames.slice(-12).map((f) => (
                    <img key={f.id} src={f.dataUrl} alt="" className="h-12 w-16 object-cover rounded-md border border-border/40 shrink-0" />
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setSource("lidar_iphone")}
                  className={cn(
                    "text-left p-2 rounded-lg border text-[10px]",
                    source === "lidar_iphone" ? "border-orange-400/40 bg-orange-500/10" : "border-border/30"
                  )}
                >
                  <Smartphone className="w-3.5 h-3.5 text-orange-300 mb-1" />
                  <span className="font-bold">LiDAR iPhone</span>
                  <p className="text-muted-foreground">Habitación → GLB</p>
                </button>
                <button
                  type="button"
                  onClick={() => setSource("meshroom_pc")}
                  className={cn(
                    "text-left p-2 rounded-lg border text-[10px]",
                    source === "meshroom_pc" ? "border-cyan-400/40 bg-cyan-500/10" : "border-border/30"
                  )}
                >
                  <Monitor className="w-3.5 h-3.5 text-cyan-300 mb-1" />
                  <span className="font-bold">Meshroom PC</span>
                  <p className="text-muted-foreground">Tras el ZIP</p>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Import mesh + CAD */}
        <section className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Importar malla (post‑Meshroom / LiDAR)
            </p>
            <div
              className="rounded-xl border border-dashed border-cyan-500/35 bg-cyan-500/5 p-4 text-center cursor-pointer hover:bg-cyan-500/10"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onFiles(e.dataTransfer.files);
              }}
            >
              <Upload className="w-5 h-5 text-cyan-300 mx-auto mb-2" />
              <p className="text-xs font-bold">GLB · OBJ · STL · PLY</p>
              <input ref={inputRef} type="file" accept=".glb,.gltf,.obj,.stl,.ply,.fbx" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            </div>
            {assets.length > 0 && (
              <div className="space-y-1 max-h-28 overflow-y-auto">
                {assets.map((a) => (
                  <div
                    key={a.id}
                    onClick={() => setSelectedId(a.id)}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer",
                      selected?.id === a.id ? "border-cyan-500/40 bg-cyan-500/10" : "border-border/30"
                    )}
                  >
                    <FileCode className="w-3 h-3 shrink-0" />
                    <span className="flex-1 truncate font-semibold">{a.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setAssets((p) => p.filter((x) => x.id !== a.id)); }}>
                      <Trash2 className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {selected && (selected.format === "glb" || selected.format === "gltf") && (
              <div className="rounded-xl border border-border/40 overflow-hidden bg-black/40">
                <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground flex items-center gap-1 border-b border-border/30">
                  <Eye className="w-3 h-3" /> Viewer 3D (GLB)
                </div>
                <model-viewer
                  src={selected.dataUrl}
                  alt={selected.name}
                  camera-controls
                  touch-action="pan-y"
                  style={{ width: "100%", height: "220px", background: "transparent" }}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Destino CAD</p>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(FORMAT_GUIDE) as CadTarget[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setCadTarget(k)}
                  className={cn(
                    "px-2.5 py-1 rounded-lg text-[10px] font-bold border",
                    cadTarget === k ? "bg-primary/20 border-primary/40 text-primary" : "border-border/40 text-muted-foreground"
                  )}
                >
                  {FORMAT_GUIDE[k].title}
                </button>
              ))}
            </div>
            <div className="rounded-xl border border-border/40 bg-muted/15 p-3 text-[10px] space-y-1">
              <p className="font-bold text-foreground flex items-center gap-1"><Box className="w-3.5 h-3.5" /> {guide.title}</p>
              <p className="text-muted-foreground">{guide.tip}</p>
              <p className="text-cyan-200/90">{guide.importHint}</p>
            </div>
            <button
              type="button"
              onClick={downloadCadPack}
              className="h-9 px-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-[11px] font-extrabold inline-flex items-center gap-1.5"
            >
              Pack → {guide.title} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        {/* 1+2 A/B + generative */}
        <section className="space-y-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5" /> 1–2 · Prueba A/B + demo generativa
          </p>
          <div className="grid lg:grid-cols-2 gap-3">
            <div className="rounded-xl border border-border/40 p-3 space-y-2">
              <p className="text-[11px] font-bold">Métrico (Meshroom)</p>
              <p className="text-[10px] text-muted-foreground">ZIP → Meshroom → importa GLB/OBJ arriba. Anota resultado:</p>
              <textarea
                value={abNotes.metric}
                onChange={(e) => setAbNotes((p) => ({ ...p, metric: e.target.value }))}
                placeholder="¿Sirve para medir/fabricar? Huecos? Escala?"
                className="w-full h-20 text-[11px] rounded-lg bg-background border border-border/50 p-2"
              />
              {selected && (selected.format === "glb" || selected.format === "gltf") ? (
                <p className="text-[10px] text-emerald-300">Malla cargada: {selected.name}</p>
              ) : (
                <p className="text-[10px] text-muted-foreground">Importa GLB para ver viewer.</p>
              )}
            </div>
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-3 space-y-2">
              <p className="text-[11px] font-bold text-violet-100">Generativo (estilo Astra / Images 2.5)</p>
              <div className="flex flex-wrap gap-1.5">
                {(["product", "property", "structure"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setGenKind(k)}
                    className={cn(
                      "px-2 py-0.5 rounded text-[10px] font-bold border",
                      genKind === k ? "border-violet-400/50 bg-violet-500/20" : "border-border/40"
                    )}
                  >
                    {k}
                  </button>
                ))}
              </div>
              <input
                value={genSubject}
                onChange={(e) => setGenSubject(e.target.value)}
                className="w-full h-8 text-[11px] rounded-lg bg-background border border-border/50 px-2"
                placeholder="Sujeto (ej. cisterna, fachada, bracket)"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={genBusy || frames.length === 0}
                  onClick={runGenerativeDemo}
                  className="h-8 px-3 rounded-lg bg-violet-600 text-white text-[10px] font-bold disabled:opacity-40"
                >
                  {genBusy ? "Generando…" : "Generar vista demo"}
                </button>
                <button type="button" onClick={openAstraPrompt} className="h-8 px-3 rounded-lg border border-border/50 text-[10px] font-bold">
                  Abrir prompt en chat
                </button>
              </div>
              {genResult?.dataUrl || genResult?.url ? (
                <img
                  src={genResult.dataUrl || genResult.url}
                  alt="generative"
                  className="w-full max-h-40 object-contain rounded-lg border border-border/40"
                />
              ) : null}
              <textarea
                value={abNotes.generative}
                onChange={(e) => setAbNotes((p) => ({ ...p, generative: e.target.value }))}
                placeholder="¿Se ve casi perfecto? ¿Útil solo visual?"
                className="w-full h-16 text-[11px] rounded-lg bg-background border border-border/50 p-2"
              />
              {genResult?.disclaimer && (
                <p className="text-[9px] text-violet-200/70">{genResult.disclaimer}</p>
              )}
            </div>
          </div>
          <input
            value={abNotes.winner}
            onChange={(e) => setAbNotes((p) => ({ ...p, winner: e.target.value }))}
            placeholder="Veredicto A/B: ¿métrico, generativo, o ambos (visual + CAD)?"
            className="w-full h-9 text-[11px] rounded-xl bg-muted/20 border border-border/40 px-3"
          />
        </section>

        {/* B — Hole closing */}
        <section className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Wrench className="w-3.5 h-3.5" /> B · Cerrar huecos (después de Meshroom)
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {HOLE_CHECKLIST.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setHoleDone((p) => ({ ...p, [item.id]: !p[item.id] }))}
                className={cn(
                  "text-left p-3 rounded-xl border transition-colors",
                  holeDone[item.id] ? "border-emerald-500/40 bg-emerald-500/10" : "border-border/40 bg-muted/10"
                )}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <CheckCircle2 className={cn("w-3.5 h-3.5", holeDone[item.id] ? "text-emerald-400" : "text-muted-foreground")} />
                  <span className="text-[11px] font-bold">{item.title}</span>
                </div>
                <p className="text-[10px] text-muted-foreground leading-snug">{item.detail}</p>
              </button>
            ))}
          </div>
          <div className="flex items-start gap-1.5 text-[10px] text-amber-200/90">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Si faltan caras enteras, vuelve a capturar esos ángulos — el remesh no inventa geometría real como un sólido CAD.
          </div>
          <a
            href="https://alicevision.org/#meshroom"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground hover:text-cyan-300"
          >
            Meshroom docs <ExternalLink className="w-3 h-3" />
          </a>
        </section>
      </div>
    </div>
  );
}
